import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useInvoiceGrid from "@/features/invoices/hooks/use-invoice-grid";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { DenseDepositInvoice } from "@/features/invoices/deposit-invoices/deposit-invoice.type";
import { useEffect, useMemo, useState } from "react";
import { DepositInvoiceRow } from "@/features/invoices/deposit-invoices/deposit-invoice-row.type";
import { depositInvoiceRowMapper } from "@/features/invoices/utils/deposit-invoice-row-mapper";
import useIdentity from "@/features/ui/hooks/use-identity";

interface DepositInvoiceGridProps {
  resources?: DenseDepositInvoice[];
  mutateResources: () => void;
}

export default function DepositInvoiceGrid({
  resources,
  mutateResources,
}: DepositInvoiceGridProps) {
  const identity = useIdentity();
  const [rows, setRows] = useState<DepositInvoiceRow[]>();
  const invoiceGrid = useInvoiceGrid({ rows, mutateRows: mutateResources });

  const rowMapper = useMemo(() => {
    return depositInvoiceRowMapper.copy();
  }, []);

  useEffect(() => {
    setRows((prev) => {
      return rowMapper.toRows(
        identity?.company?.type !== "BUYER"
          ? resources?.map((resource) => {
              return {
                ...resource,
                isRead: false,
              };
            })
          : resources,
        prev
      );
    });
  }, [resources, rowMapper, identity]);

  const { navigate } = useRouterHelper();

  switch (identity?.company?.type) {
    case "AGENCY":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "DepositInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                // invoiceGrid.pairs.issuedThrough.column,
                invoiceGrid.pairs.orderedBy.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.amount.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                // invoiceGrid.pairs.issuedThrough.field,
                invoiceGrid.pairs.orderedBy.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.amount.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
              onClick: (row) => {
                navigate(`/deposit-invoices/${row.id}`);
              },
            },
          ]}
        />
      );
    case "BUYER":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "DepositInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                invoiceGrid.pairs.issuedThrough.column,
                // invoiceGrid.pairs.orderedBy.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.amount.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                invoiceGrid.pairs.issuedThrough.field,
                // invoiceGrid.pairs.orderedBy.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.number.field,
                invoiceGrid.pairs.amount.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
              onClick: (row) => {
                navigate(`/deposit-invoices/${row.id}`);
              },
            },
          ]}
        />
      );
    default:
      return null;
  }
}
